import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid
} from '@mui/material';
import EditableItemList from './common/EditableItemList';
import { useCV } from '../contexts/CVContext';

const OnlineProfiles = () => {
  const { isDataReady, cvData, updateOnlineProfiles, readonly } = useCV();

  // Initialize with empty online profiles array
  const [onlineProfiles, setOnlineProfiles] = useState([]);

  const [expandedIndex, setExpandedIndex] = useState(null);

  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.onlineProfiles?.length > 0 && isDataReady) {
      setOnlineProfiles(cvData.onlineProfiles);
    }
  }, [cvData.onlineProfiles, isDataReady]);

  // Custom handler to update both local state and context
  const handleOnlineProfilesChange = (newProfiles) => {
    // Update the order property for each profile
    const orderedProfiles = newProfiles.map((profile, index) => ({
      ...profile,
      order: index + 1
    }));

    setOnlineProfiles(orderedProfiles);
    updateOnlineProfiles(orderedProfiles);
  };

  // Function to create a new empty online profile
  const createNewOnlineProfile = () => ({
    id: null,
    profile: '',
    order: onlineProfiles.length + 1
  });

  // Function to render the summary of an online profile (collapsed view)
  const renderOnlineProfileSummary = (profile) => (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {profile.profile || '(Not specified)'}
      </Typography>
    </>
  );

  // Function to render the edit form for an online profile (expanded view)
  const renderOnlineProfileEditForm = (profile, onChange) => (
    <Grid
      container
      spacing={2}
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px',
        '& .MuiGrid-item': {
          padding: '0',
          width: '100%'
        }
      }}
    >
      <Grid item>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Profile URL <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={profile.profile || ''}
            onChange={(e) => {
              onChange({
                ...profile,
                profile: e.target.value
              });
            }}
            placeholder="e.g., https://linkedin.com/in/your-profile"
            disabled={readonly}
            sx={{ backgroundColor: 'white' }}
            helperText="Enter the URL to your LinkedIn, GitHub, portfolio, or other professional profile"
          />
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <EditableItemList
      items={onlineProfiles}
      setItems={handleOnlineProfilesChange}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      createNewItem={createNewOnlineProfile}
      renderSummary={renderOnlineProfileSummary}
      renderEditForm={renderOnlineProfileEditForm}
      addButtonText={onlineProfiles.length > 0 ? "Add another profile" : "Add an online profile"}
      emptyState={{
        title: "You haven't added any online profiles yet.",
        description: "Add links to your LinkedIn, GitHub, portfolio website, or other professional online profiles."
      }}
      itemName="online profile"
      readonly={readonly}
    />
  );
};

export default OnlineProfiles;
